import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  ShoppingCart,
  ClipboardList,
  LifeBuoy,
  ChevronDown,
  Sparkles,
  X,
  CheckCircle2,
  Briefcase,
  Building,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Link } from "react-router-dom";

import ProductCard from "../components/infoshop/ProductCard";
import ProfileGate from "../components/infoshop/ProfileGate";
import RFQForm from "../components/infoshop/RFQForm";
import SupportForm from "../components/infoshop/SupportForm";
import CartDrawer from "../components/infoshop/CartDrawer";
import OrderHistory from "../components/infoshop/OrderHistory";
import OTPDialog from "../components/infoshop/OTPDialog";
import CompareDialog from "../components/infoshop/CompareDialog";
import SpecSheetModal from "../components/infoshop/SpecSheetModal";
import TaxExemptionsDialog from "../components/infoshop/TaxExemptionsDialog";
import SubSupplierTiles from "../components/infoshop/SubSupplierTiles";
import TenantBrandStrip from "../components/infoshop/TenantBrandStrip";
import TenantWelcomeCard from "../components/infoshop/TenantWelcomeCard";
import PersonaSwitcher from "../components/infoshop/PersonaSwitcher";
import FacetPanel from "../components/infoshop/FacetPanel";
import SidebarBanners from "../components/infoshop/SidebarBanners";
import ChatWidget from "../components/infoshop/ChatWidget";
import CompareFloatingBar from "../components/infoshop/CompareFloatingBar";
import { getTenantBrand, displayCompany } from "../branding/tenants";
import { COUNTRY_FLAGS } from "../components/infoshop/utils";
import Flag from "../components/Flag";
import { useLocale } from "../i18n/LocaleContext";
import { PERSONAS, PERSONA_KEYS } from "../i18n/personas";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InfoShopPage() {
  const { persona, personaKey, setPersonaKey, t } = useLocale();
  const auth = useAuth();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showProfileGate, setShowProfileGate] = useState(false);
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  // ─── PunchOut welcome chip ──────────────────────────────────────────
  // Buyers who arrive via an origin ERP PunchOut (?punchout_session=…)
  // skip the login gate — show a one-time "you landed correctly" chip
  // under the header so the handoff from Coupa/Ariba feels intentional.
  // Danone sessions present as Coupa (Aerchain stays invisible).
  const [punchoutWelcome, setPunchoutWelcome] = useState(null);
  const [punchoutWelcomeDismissed, setPunchoutWelcomeDismissed] = useState(false);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("punchout_session");
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/punchout/session/${sid}`);
        if (!cancelled) setPunchoutWelcome(data);
      } catch {
        if (!cancelled) setPunchoutWelcome(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const punchoutBuyerName = (() => {
    const raw = punchoutWelcome?.network_id || "";
    if (!raw) return "there";
    const local = raw.includes("@") ? raw.split("@")[0] : raw;
    const pretty = local
      .split(/[._\-\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
    return pretty || raw;
  })();
  const punchoutOrigin = (() => {
    const idStr = [
      punchoutWelcome?.from_identity,
      punchoutWelcome?.origin_label,
      punchoutWelcome?.company_name,
      punchoutWelcome?.network_id,
    ].filter(Boolean).join(" ").toUpperCase();
    return idStr.includes("DANONE") ? "Coupa" : (punchoutWelcome?.origin_label || "your ERP");
  })();

  // Catalog state
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ categories: [], brands: [] });
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [brandFilter, setBrandFilter] = useState(null);
  const [searching, setSearching] = useState(false);

  // Drawers / dialogs
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState({ lines: [], cart_id: null, max: 6 });
  const [transferring, setTransferring] = useState(false);

  const [ordersOpen, setOrdersOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [openedOrder, setOpenedOrder] = useState(null);

  const [rfqOpen, setRfqOpen] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [rfqDefaults, setRfqDefaults] = useState(null);

  // Compare: up to 4 UniBuy™ PNs
  const COMPARE_MAX = 4;
  const [comparePns, setComparePns] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  // In-app Spec Sheet popup (replaces the previous new-tab behavior)
  const [specPn, setSpecPn] = useState(null);

  // Tax Exemption certificates (Infosys Procurement Tax × Customer Legal/Tax)
  const [exemptionsOpen, setExemptionsOpen] = useState(false);

  const [supportOpen, setSupportOpen] = useState(false);
  const [supportDefaults, setSupportDefaults] = useState(null);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([
    { from: "bot", text: "Hi! I'm the UniBuy™ assistant. Ask me about your orders, search the catalog, or raise a support request." },
  ]);
  const [chatInput, setChatInput] = useState("");

  // Bootstrap (and re-bootstrap whenever persona changes)
  const bootstrapForPersona = async (p, opts = {}) => {
    try {
      // URL overrides win on the very first mount so deep links keep working;
      // on subsequent persona switches we ignore the URL.
      const overrides = { ...opts.urlOverrides };
      const keysToInject = ["network_id", "company_name", "country_name", "country_code", "erp_system"];
      keysToInject.forEach((k) => {
        if (overrides[k] === undefined) overrides[k] = p[k];
      });

      const { data: s } = await axios.get(`${API}/infoshop/session`, { params: overrides });
      setSession(s);

      // Push persona to the URL so the dashboard / deep links see the same identity.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("network_id", s.network_id);
        url.searchParams.set("company_name", s.company_name);
        url.searchParams.set("country_code", s.country_code);
        url.searchParams.set("country_name", s.country_name);
        window.history.replaceState({}, "", url.toString());
      } catch (err) { console.warn("[UniBuy™] could not rewrite URL params:", err); }

      const { data: pr } = await axios.get(`${API}/infoshop/profile`, {
        params: { network_id: s.network_id, company_name: s.company_name },
      });
      // Force the profile gate on every persona switch so the onboarding flow
      // is part of the demo. On first mount, only show it when profile is missing.
      // v3.9.21: PunchOut arrivals skip the gate entirely — their identity
      // already came from the POSR, and the hard modal blocked the catalog
      // (search bar inert behind the overlay). They can still complete the
      // profile later via the Edit Profile menu.
      const isPunchoutArrival = !!new URLSearchParams(window.location.search).get("punchout_session");
      if (!isPunchoutArrival && (opts.forceProfileGate || !pr.complete)) {
        setShowProfileGate(true);
        setProfile(pr.profile || null);
      } else {
        setShowProfileGate(false);
        setProfile(pr.profile);
      }
      await Promise.all([loadProducts("", s), loadFacets("", s), loadCart(s), loadOrders(s)]);
    } catch (e) {
      console.error(e);
      toast.error("Failed to initialize UniBuy™ session");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const overrides = {};
    ["network_id", "company_name", "country_name", "country_code", "erp_system"].forEach((k) => {
      const v = params.get(k);
      if (v) overrides[k] = v;
    });

    // Honor ?otp=1 deep link from the Landing page → open OTP modal once session is up.
    const wantsOtp = params.get("otp") === "1";
    const triggerOtp = () => {
      if (!wantsOtp) return;
      setTimeout(() => setOtpOpen(true), 400);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("otp");
        window.history.replaceState({}, "", url.toString());
      } catch (err) { console.warn("[UniBuy™] could not strip otp= flag from URL:", err); }
    };

    // If the URL identity matches a different persona, switch silently so the
    // header chip + locale + currency reflect the visitor.
    if (overrides.network_id) {
      const matched = PERSONA_KEYS.find(
        (k) => PERSONAS[k].network_id.toLowerCase() === overrides.network_id.toLowerCase()
      );
      if (matched && matched !== personaKey) {
        setPersonaKey(matched);
        bootstrapForPersona(PERSONAS[matched], { urlOverrides: overrides });
        triggerOtp();
        return;
      }
    }
    bootstrapForPersona(persona, { urlOverrides: overrides });
    triggerOtp();
     
  }, []);

  const switchPersona = async (nextKey) => {
    if (!nextKey || nextKey === personaKey) return;
    const next = PERSONAS[nextKey];
    if (!next) return;
    setPersonaKey(nextKey);
    // Reset volatile UI state so the previous persona's data doesn't flash through.
    setCart({ lines: [], cart_id: null, max: 6 });
    setOrders([]);
    setProducts([]);
    setQuery("");
    setActiveQuery("");
    setCategoryFilter(null);
    setBrandFilter(null);
    setShowProfileMenu(false);
    setCartOpen(false);
    await bootstrapForPersona(next, { forceProfileGate: true });
    toast.success(`${next.display_name} · ${next.country_name}`);
  };

  const loadProducts = async (q, s, cat = null, brand = null) => {
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/infoshop/products`, {
        params: {
          q: q || undefined,
          category: cat || undefined,
          brand: brand || undefined,
          limit: 200,
          country_code: s?.country_code || session?.country_code || undefined,
          network_id: s?.network_id || session?.network_id || undefined,
          company_name: s?.company_name || session?.company_name || undefined,
        },
      });
      setProducts(data.items);
    } finally {
      setSearching(false);
    }
  };
  const loadFacets = async (q) => {
    const { data } = await axios.get(`${API}/infoshop/facets`, { params: { q: q || undefined } });
    setFacets(data);
  };
  const loadCart = async (s = session) => {
    if (!s) return;
    const { data } = await axios.get(`${API}/infoshop/cart`, {
      params: { network_id: s.network_id, company_name: s.company_name },
    });
    setCart(data);
  };
  const loadOrders = async (s = session) => {
    if (!s) return;
    const { data } = await axios.get(`${API}/infoshop/orders`, {
      params: { network_id: s.network_id, company_name: s.company_name },
    });
    setOrders(data.orders);
  };

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    setActiveQuery(q);
    setCategoryFilter(null);
    setBrandFilter(null);
    await Promise.all([loadProducts(q, session), loadFacets(q)]);
  };
  const clearSearch = async () => {
    setQuery("");
    setActiveQuery("");
    setCategoryFilter(null);
    setBrandFilter(null);
    await Promise.all([loadProducts("", session), loadFacets("")]);
  };
  const applyCategory = async (name) => {
    const next = categoryFilter === name ? null : name;
    setCategoryFilter(next);
    await loadProducts(activeQuery, session, next, brandFilter);
  };
  const applyBrand = async (name) => {
    const next = brandFilter === name ? null : name;
    setBrandFilter(next);
    await loadProducts(activeQuery, session, categoryFilter, next);
  };

  const saveProfile = async (payload) => {
    setProfileSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/infoshop/profile`, {
        ...payload,
        network_id: session.network_id,
        company_name: session.company_name,
        country_name: session.country_name,
        country_code: session.country_code,
        erp_system: session.erp_system,
      });
      setProfile(data.profile);
      setShowProfileGate(false);
      setEditProfileOpen(false);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save profile");
    } finally {
      setProfileSubmitting(false);
    }
  };

  const addToCart = async (product, quantity) => {
    try {
      const { data } = await axios.post(
        `${API}/infoshop/cart`,
        { infoshop_pn: product.infoshop_pn, quantity },
        { params: { network_id: session.network_id, company_name: session.company_name } }
      );
      setCart(data);
      toast.success(`Added ${product.infoshop_pn} × ${quantity}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add to cart");
    }
  };
  const changeQty = async (pn, qty) => {
    try {
      const { data } = await axios.post(
        `${API}/infoshop/cart`,
        { infoshop_pn: pn, quantity: qty },
        { params: { network_id: session.network_id, company_name: session.company_name } }
      );
      setCart(data);
    } catch (err) { toast.error("Update failed"); }
  };
  const removeFromCart = async (pn) => {
    const { data } = await axios.delete(`${API}/infoshop/cart/${pn}`, {
      params: { network_id: session.network_id, company_name: session.company_name },
    });
    setCart(data);
  };

  const transferCart = async (opts = {}) => {
    setTransferring(true);
    try {
      const { data } = await axios.post(`${API}/infoshop/orders/transfer`, {
        network_id: session.network_id,
        company_name: session.company_name,
        country_code: session.country_code,
        destination: opts.destination || null,
      });
      await Promise.all([loadCart(), loadOrders()]);
      toast.success(`Order ${data.order_id} created`);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
      return null;
    } finally { setTransferring(false); }
  };

  const openOrderDetail = async (id) => {
    const { data } = await axios.get(`${API}/infoshop/orders/${id}`);
    setOpenedOrder(data);
  };

  const submitRfq = async (form) => {
    const { data } = await axios.post(`${API}/infoshop/rfq`, {
      ...form,
      network_id: session.network_id,
      company_name: session.company_name,
      country_code: session.country_code,
      source: rfqDefaults?.source || "manual",
      source_infoshop_pn: rfqDefaults?.source_infoshop_pn || null,
    });
    toast.success(`${data.rfq_id} submitted`);
    loadOrders(); // surface RFQ in Order History
    return data;
  };

  const submitSupport = async (form) => {
    const { data } = await axios.post(`${API}/infoshop/support`, {
      ...form,
      network_id: session.network_id,
      company_name: session.company_name,
    });
    toast.success(`${data.ticket_id} created`);
    return data;
  };

  // Derived UI
  const flag = session ? COUNTRY_FLAGS[session.country_code] || "🌐" : "";
  const noResults = activeQuery && products.length === 0;
  const limitedResults = activeQuery && products.length > 0 && products.length < 3;
  const tenantBrand = getTenantBrand(auth?.user, session);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Tenant brand strip — only shown for tenants with bespoke branding
          (Danone, JDE Peet's). Sits above the UniBuy™ chrome so it
          reads as an Infosys-issued co-brand banner. */}
      {tenantBrand && <TenantBrandStrip brand={tenantBrand} />}
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="onesource-header-logo">
            <div className="h-8 w-8 rounded-md bg-[#0072CE] flex items-center justify-center text-white font-display font-bold">i</div>
            <span className="hidden md:inline-block h-7 w-px bg-slate-300" />
            <img
              src="/unibuy-wordmark.png"
              alt="UniBuy™ by Infosys · Intelligent Sourcing and Procurement Solution"
              className="hidden md:block h-9 lg:h-10 w-auto max-w-[150px] lg:max-w-[175px] object-contain object-left"
            />
            <div className="leading-tight md:hidden">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">{t("header.title")}</div>
              <div className="font-display font-bold text-slate-900 text-sm">{t("header.subtitle")}</div>
            </div>
          </Link>

          {/* Demo Persona switcher — drives locale / currency / selling entity */}
          <PersonaSwitcher
            persona={persona}
            personaKey={personaKey}
            switchPersona={switchPersona}
            t={t}
          />

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-[180px] lg:min-w-[420px] 2xl:min-w-[520px] max-w-3xl">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("catalog.search_placeholder")}
                data-testid="infoshop-search-input"
                className="pl-9 pr-24 h-10 bg-slate-50 border-slate-200 focus-visible:ring-[#0072CE]"
              />
              {activeQuery && (
                <button type="button" onClick={clearSearch} className="absolute right-20 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900" aria-label="Clear search" data-testid="infoshop-search-clear">
                  <X size={14} />
                </button>
              )}
              <Button type="submit" data-testid="infoshop-search-submit" className="absolute right-1 top-1/2 -translate-y-1/2 h-8 bg-[#0072CE] hover:bg-[#005BA6] text-white text-xs">{t("catalog.search_btn")}</Button>
            </div>
          </form>

          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" onClick={() => setOrdersOpen(true)} data-testid="open-orders-btn" className="text-slate-700 px-2.5" title={t("header.orders")} aria-label={t("header.orders")}>
              <ClipboardList size={16} /><span className="hidden 2xl:inline ml-2">{t("header.orders")}</span>
            </Button>
            <Button variant="ghost" onClick={() => { setSupportDefaults(null); setSupportOpen(true); }} data-testid="open-support" className="text-slate-700 px-2.5" title={t("header.support")} aria-label={t("header.support")}>
              <LifeBuoy size={16} /><span className="hidden 2xl:inline ml-2">{t("header.support")}</span>
            </Button>
            <Button onClick={() => setCartOpen(true)} data-testid="open-cart" className="bg-slate-900 hover:bg-slate-800 text-white relative">
              <ShoppingCart size={16} /><span className="hidden sm:inline ml-2">{t("header.cart")}</span>
              {cart.lines.length > 0 && (
                <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-amber-400 text-slate-900 text-[10px] font-bold flex items-center justify-center">{cart.lines.length}</span>
              )}
            </Button>

            {/* User */}
            {session && (
              <DropdownMenu open={showProfileMenu} onOpenChange={setShowProfileMenu}>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 pl-2 pr-2 py-1.5 rounded-md hover:bg-slate-100 max-w-[190px] xl:max-w-[240px]">
                    <Flag code={session.country_code} height={18} />
                    <div className="text-left leading-tight hidden md:block min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("user.logged_in")}</div>
                      {/* Long PunchOut buyer emails (e.g. firstname.lastname@danone.com)
                          truncate with an ellipsis so the chip never squeezes the
                          search bar — full value on hover tooltip and in the dropdown. */}
                      <div className="text-sm font-semibold text-slate-900 font-mono truncate" title={session.network_id} data-testid="user-menu-network-id">{session.network_id}</div>
                    </div>
                    <ChevronDown size={14} className="text-slate-500 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t("user.network_id")}</div>
                    <div className="font-mono font-semibold text-slate-900 break-all">{session.network_id}</div>
                    <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t("user.company")}</div>
                    <div className="text-sm font-semibold text-slate-900">{displayCompany(session.company_name)}</div>
                    <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t("user.country")}</div>
                    <div className="text-sm text-slate-900 inline-flex items-center gap-1.5">
                      <Flag code={session.country_code} height={12} /> {session.country_name}
                    </div>
                    {profile?.business_email && (
                      <>
                        <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t("user.email")}</div>
                        <div className="text-sm text-slate-900">{profile.business_email}</div>
                      </>
                    )}
                    {profile?.title && (
                      <>
                        <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t("user.title")}</div>
                        <div className="text-sm text-slate-900">{profile.title}</div>
                      </>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setEditProfileOpen(true)} data-testid="menu-edit-profile">
                    {t("user.confirm_contact")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setOrdersOpen(true)} data-testid="menu-orders">
                    {t("user.order_history")}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to={`/infoshop/dashboard?network_id=${encodeURIComponent(session.network_id)}&company_name=${encodeURIComponent(session.company_name)}&country_code=${encodeURIComponent(session.country_code)}`}
                      data-testid="menu-dashboard"
                    >
                      {t("user.spend_dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  {auth?.user?.role === "admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/infoshop/admin/onboarding" data-testid="menu-onboarding">
                          Customer On-Boarding
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/infoshop/admin/sub-suppliers" data-testid="menu-sub-suppliers">
                          Sub-Supplier Catalog
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/infoshop/admin/supplier-onboarding" data-testid="menu-supplier-onboarding">
                          Supplier Onboarding · Category Discounts
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/infoshop/admin/punchout-sessions" data-testid="menu-punchout-sessions">
                          PunchOut Session Inspector
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/infoshop/admin/punchout-enablement" data-testid="menu-punchout-enablement">
                          Danone Coupa Enablement
                        </Link>
                      </DropdownMenuItem>
                      {/* Email Workbench link disabled — preserved on disk, see server.py */}
                      {/* <DropdownMenuItem asChild>
                        <Link to="/infoshop/admin/email" data-testid="menu-email-workbench">
                          Email workbench
                        </Link>
                      </DropdownMenuItem> */}
                    </>
                  )}
                  {session?.network_id?.toUpperCase?.().startsWith("INFOSYS.") &&
                    session?.company_name?.trim?.().toUpperCase?.() === "INFOSYS BPM" && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to={`/infoshop/admin/enrichment?network_id=${encodeURIComponent(session.network_id)}&company_name=${encodeURIComponent(session.company_name)}`} data-testid="menu-enrichment">
                            {t("user.procurement_enrichment")}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="text-xs text-slate-500">
                    {t("user.erp_label")}: {session.erp_system} · {t("user.no_direct_login")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => { try { auth?.logout?.(); } catch { /* auth context may be unmounted */ } }}
                    data-testid="menu-logout"
                    className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                  >
                    Sign out of UniBuy™
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Section nav strip — keeps the search bar full width */}
        <div className="border-t border-slate-100 bg-[#0B2A4A]">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 h-9 flex items-center gap-5 text-xs">
            <span className="inline-flex items-center gap-1.5 font-semibold text-white"><ShoppingCart size={13} /> Product catalog</span>
            <Link to="/services" data-testid="topnav-strategic-services" className="inline-flex items-center gap-1.5 font-semibold text-[#7DD3FC] hover:text-white transition-colors">
              <Briefcase size={13} /> Strategic Services Aggregator
              <span className="text-[9px] font-bold uppercase tracking-wider bg-[#00B4D8] text-[#0B2A4A] rounded px-1.5 py-0.5">New</span>
            </Link>
            <Link to="/services/c/FM" data-testid="topnav-ifm-services" className="inline-flex items-center gap-1.5 font-semibold text-[#7DD3FC] hover:text-white transition-colors">
              <Building size={13} /> Facilities Management
            </Link>
            <span className="hidden md:inline text-white/50 ml-auto">Digital Marketing · Integrated Facilities Management · AI-guided briefs</span>
          </div>
        </div>

        {/* Active filters bar */}
        {(activeQuery || categoryFilter || brandFilter) && (
          <div className="border-t border-slate-100 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 lg:px-6 py-2.5 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-500">{t("catalog.filters_label")}</span>
              {activeQuery && (
                <button onClick={clearSearch} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-slate-400">
                  {t("catalog.filter_search")} <span className="font-mono font-semibold">{activeQuery}</span> <X size={10} />
                </button>
              )}
              {categoryFilter && (
                <button onClick={() => applyCategory(categoryFilter)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-slate-400">
                  {t("catalog.filter_cat_pill")} <span className="font-semibold">{categoryFilter}</span> <X size={10} />
                </button>
              )}
              {brandFilter && (
                <button onClick={() => applyBrand(brandFilter)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-slate-400">
                  {t("catalog.filter_brand_pill")} <span className="font-semibold">{brandFilter}</span> <X size={10} />
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* PunchOut welcome chip — first arrival from an origin ERP.
          Subtle confirmation the buyer landed in the right place;
          dismissible, never blocks the catalog. */}
      {punchoutWelcome && !punchoutWelcomeDismissed && (
        <div className="border-b border-emerald-100 bg-emerald-50/70" data-testid="punchout-welcome-chip">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-2 flex items-center gap-2.5 text-xs">
            <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
            <span className="text-emerald-900">
              <span className="font-semibold" data-testid="punchout-welcome-name">Welcome, {punchoutBuyerName}</span>
              {" "}— you're shopping UniBuy™ via <span className="font-semibold" data-testid="punchout-welcome-origin">{punchoutOrigin} PunchOut</span>.
              Your cart returns to {punchoutOrigin === "Coupa" ? "Danone · Coupa" : punchoutOrigin} automatically when you transfer it.
            </span>
            <button
              type="button"
              onClick={() => setPunchoutWelcomeDismissed(true)}
              className="ml-auto text-emerald-500 hover:text-emerald-800 transition-colors"
              aria-label="Dismiss welcome message"
              data-testid="punchout-welcome-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Filters rail */}
        <aside className="lg:col-span-3 space-y-4">
          {/* Tenant welcome card — only when a bespoke brand exists for
              this signed-in customer (Danone, JDE Peet's). Sits at the
              top of the sidebar so the personalisation is the first
              thing the customer's eye lands on when entering UniBuy™. */}
          {tenantBrand && <TenantWelcomeCard brand={tenantBrand} />}

          <Link
            to="/services"
            data-testid="sidebar-strategic-services"
            className="group block rounded-xl overflow-hidden border border-[#0B2A4A]/20 bg-[#0B2A4A] text-white hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]"
          >
            <div className="relative h-20 overflow-hidden">
              <img src="/ssa-img/SIDEBAR.jpg" alt="" className="h-full w-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500" />
              <span className="absolute left-3 top-2 text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-black/55 rounded px-1.5 py-0.5">New · Strategic Services</span>
            </div>
            <div className="p-4">
              <div className="font-display font-semibold leading-snug">Digital Marketing & Facilities Management</div>
              <div className="text-xs text-white/70 mt-1">37 scope-based services · AI category intelligence · two competitive partner quotations</div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#00B4D8]">Open <ArrowRight size={12} /></div>
            </div>
          </Link>

          <FacetPanel
            title={t("catalog.filter_category")}
            items={facets.categories}
            active={categoryFilter}
            onPick={applyCategory}
            testidPrefix="facet-cat"
          />
          <FacetPanel
            title={t("catalog.filter_brand")}
            items={facets.brands}
            active={brandFilter}
            onPick={applyBrand}
            testidPrefix="facet-brand"
          />

          <SidebarBanners
            t={t}
            onOpenRfq={() => { setRfqDefaults({ source: "manual" }); setRfqOpen(true); }}
            onOpenOtp={() => setOtpOpen(true)}
            onOpenExemptions={() => setExemptionsOpen(true)}
          />

          {/* ── Sub-Supplier PunchOut Tiles ── Second purchasing path
              (after the UniBuy™ hosted catalog above). Items chosen
              from any sub-supplier land back here as auto-created
              UniBuy™ SKUs and merge into the same cart. */}
          <SubSupplierTiles session={session} />
        </aside>

        {/* Products area */}
        <section className="lg:col-span-9">
          {/* Headline result line */}
          <div className="flex items-end justify-between mb-4">
            <div>
              <h1 className="font-display text-2xl font-semibold text-slate-900 tracking-tight">
                {activeQuery ? `${t("catalog.results_for")} "${activeQuery}"` : t("catalog.heading")}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {searching ? (
                  t("catalog.searching")
                ) : activeQuery || categoryFilter || brandFilter ? (
                  <>
                    <span className="font-semibold text-slate-700">{products.length.toLocaleString()}</span>
                    {" "}
                    {products.length === 1 ? t("catalog.match_singular") : t("catalog.match_plural")}
                    {" "}
                    <span className="font-semibold text-slate-700">3.2 Million</span>
                    {" "}
                    {t("catalog.skus")}
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-slate-700">{t("catalog.total")}</span>
                    <span className="ml-2 text-slate-400">· {t("catalog.tagline")}</span>
                  </>
                )}
                {products.some((p) => p.match_score === 100) && (
                  <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-semibold">· {t("catalog.match_100")}</span>
                )}
              </p>
            </div>
          </div>

          {/* Empty / limited results banner */}
          {(noResults || limitedResults) && (
            <div className="mb-5 rounded-xl bg-amber-50 border border-amber-200 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="h-10 w-10 rounded-md bg-amber-100 border border-amber-200 text-amber-800 flex items-center justify-center shrink-0">
                <Sparkles size={18} />
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-slate-900">
                  {noResults ? t("catalog.no_match_title") : t("catalog.limited_title")}
                </div>
                <div className="text-sm text-slate-600 mt-0.5">
                  {t("catalog.no_match_help")}
                </div>
              </div>
              <Button
                onClick={() => { setRfqDefaults({ source: "search-no-result", product_description: activeQuery }); setRfqOpen(true); }}
                data-testid="rfq-from-noresult"
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              >
                {t("catalog.submit_rfq_btn")}
              </Button>
            </div>
          )}

          {/* Product grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {products.map((p) => (
              <ProductCard
                key={p.infoshop_pn}
                product={p}
                onAdd={addToCart}
                onSubmitRfq={(prod) => { setRfqDefaults({ source: "product-rfq", source_infoshop_pn: prod.infoshop_pn, product_description: prod.description, brand_or_mfg: prod.brand, oem_mfg_part_number: prod.mfg_pn }); setRfqOpen(true); }}
                onOpenSpec={(prod) => setSpecPn(prod.infoshop_pn)}
                compareSelected={comparePns.includes(p.infoshop_pn)}
                compareDisabled={comparePns.length >= COMPARE_MAX}
                onToggleCompare={(prod) => {
                  setComparePns((prev) => {
                    if (prev.includes(prod.infoshop_pn)) {
                      return prev.filter((x) => x !== prod.infoshop_pn);
                    }
                    if (prev.length >= COMPARE_MAX) {
                      toast.error(t("compare.limit_reached"));
                      return prev;
                    }
                    return [...prev, prod.infoshop_pn];
                  });
                }}
              />
            ))}
          </div>

          {!products.length && !noResults && !searching && (
            <div className="text-sm text-slate-500 py-12 text-center">Try searching for a part number, brand or description.</div>
          )}
        </section>
      </main>

      {/* Floating chat */}
      <ChatWidget
        chatOpen={chatOpen}
        setChatOpen={setChatOpen}
        chatMsgs={chatMsgs}
        setChatMsgs={setChatMsgs}
        chatInput={chatInput}
        setChatInput={setChatInput}
        onRaiseSupport={() => { setSupportDefaults(null); setSupportOpen(true); setChatOpen(false); }}
      />

      {/* Dialogs / drawers */}
      <ProfileGate
        open={showProfileGate || editProfileOpen}
        session={session}
        onSave={saveProfile}
        submitting={profileSubmitting}
        dismissible={!!punchoutWelcome || editProfileOpen}
        onDismiss={() => { setShowProfileGate(false); setEditProfileOpen(false); }}
      />
      <CartDrawer
        open={cartOpen}
        onOpenChange={setCartOpen}
        cart={cart}
        onChangeQty={changeQty}
        onRemove={removeFromCart}
        onTransfer={transferCart}
        transferring={transferring}
        persona={persona}
      />
      <OrderHistory
        open={ordersOpen}
        onOpenChange={(o) => { setOrdersOpen(o); if (!o) setOpenedOrder(null); }}
        orders={orders}
        openedOrder={openedOrder}
        onOpenOrder={openOrderDetail}
        onCloseOrder={() => setOpenedOrder(null)}
        onRaiseSupport={(d) => { setSupportDefaults(d); setSupportOpen(true); }}
      />
      <RFQForm
        open={rfqOpen}
        onOpenChange={setRfqOpen}
        defaults={rfqDefaults}
        onSubmit={submitRfq}
      />
      <SupportForm
        open={supportOpen}
        onOpenChange={setSupportOpen}
        defaults={supportDefaults}
        onSubmit={submitSupport}
      />
      <OTPDialog
        open={otpOpen}
        onOpenChange={setOtpOpen}
        session={session}
        onSubmitted={() => loadOrders()}
      />
      <CompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        pns={comparePns}
        onAdd={(p, q) => { addToCart(p, q); }}
        onRemovePn={(pn) => setComparePns((prev) => prev.filter((x) => x !== pn))}
        onClearAll={() => { setComparePns([]); setCompareOpen(false); }}
        onOpenSpec={(pn) => setSpecPn(pn)}
      />

      {/* Spec Sheet popup (replaces the legacy new-tab behavior) */}
      <SpecSheetModal
        open={!!specPn}
        onOpenChange={(o) => { if (!o) setSpecPn(null); }}
        infoshopPn={specPn}
        countryCode={session?.country_code}
      />

      {/* Tax Exemption Certificates · Infosys Tax × Customer Legal/Tax */}
      <TaxExemptionsDialog
        open={exemptionsOpen}
        onOpenChange={setExemptionsOpen}
        session={session}
        onChanged={() => loadProducts(activeQuery, session, categoryFilter, brandFilter)}
      />

      {/* Floating Compare bar — appears whenever 1+ product is queued for compare */}
      <CompareFloatingBar
        t={t}
        comparePns={comparePns}
        compareMax={COMPARE_MAX}
        onClearAll={() => setComparePns([])}
        onOpen={() => setCompareOpen(true)}
      />
    </div>
  );
}
